import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Instagram,
  Loader2,
  User,
  DollarSign,
  MessageSquare,
  Video,
  Mail,
  MessageCircle,
  Save,
  Trash2,
  ExternalLink,
  Play,
  Square,
  Camera,
  RefreshCw,
  Check,
  Copy,
  Upload,
  Download,
  Image as ImageIcon,
  Film,
  Users,
  Link as LinkIcon,
  Zap,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Gem,
} from 'lucide-react';
import api from '../lib/api';

// TikTok and YouTube icons as SVG components
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const YouTubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CreatorPackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, getToken } = useAuth();
  
  // Get the actual token (from context or localStorage)
  const authToken = token || getToken?.() || localStorage.getItem('token');
  
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  
  // Form states
  const [about, setAbout] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [youtubeHandle, setYoutubeHandle] = useState('');
  const [followersCount, setFollowersCount] = useState('');
  const [designCostAmount, setDesignCostAmount] = useState('');
  const [designCostNotes, setDesignCostNotes] = useState('');
  const [emailCopy, setEmailCopy] = useState('');
  const [dmCopy, setDmCopy] = useState('');
  const [status, setStatus] = useState('draft');
  
  // Design files state
  const [designFiles, setDesignFiles] = useState([]);
  const [uploadingDesignFile, setUploadingDesignFile] = useState(false);
  const designFileInputRef = useRef(null);
  
  // Brainstorm state
  const [brainstorms, setBrainstorms] = useState([]);
  const [brainstorming, setBrainstorming] = useState(false);
  const [expandedBrainstorm, setExpandedBrainstorm] = useState(null);
  
  // Video recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [deletingVideo, setDeletingVideo] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    fetchCreator();
    return () => {
      stopCamera();
    };
  }, [id]);

  const fetchCreator = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/creator-outreach/${id}`);
      const data = response.data;
      setCreator(data);
      
      // Populate form fields
      setAbout(data.about || '');
      setTiktokHandle(data.tiktok_handle || '');
      setYoutubeHandle(data.youtube_handle || '');
      setFollowersCount(data.followers_count?.toString() || '');
      setDesignCostAmount(data.design_cost?.amount?.toString() || '');
      setDesignCostNotes(data.design_cost?.notes || '');
      setDesignFiles(data.design_files || []);
      setEmailCopy(data.pitch?.email_copy || '');
      setDmCopy(data.pitch?.dm_copy || '');
      setStatus(data.status || 'draft');
      
      // Fetch brainstorms
      fetchBrainstorms();
    } catch (error) {
      console.error('Error fetching creator:', error);
      toast.error('Failed to load creator');
      navigate('/creator-outreach');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/creator-outreach/${id}`, {
        about,
        tiktok_handle: tiktokHandle,
        youtube_handle: youtubeHandle,
        followers_count: parseInt(followersCount) || 0,
        design_cost_amount: parseFloat(designCostAmount) || 0,
        design_cost_notes: designCostNotes,
        email_copy: emailCopy,
        dm_copy: dmCopy,
        status
      });
      toast.success('Changes saved!');
      fetchCreator();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Brainstorm functions
  const fetchBrainstorms = async () => {
    try {
      const response = await api.get(`/creator-outreach/${id}/brainstorm`);
      setBrainstorms(response.data || []);
    } catch (error) {
      console.error('Error fetching brainstorms:', error);
    }
  };

  const handleBrainstorm = async () => {
    if (!about || about.trim().length < 20) {
      toast.error('Please add research notes in the About section first (at least 20 characters)');
      setActiveTab('about');
      return;
    }

    try {
      setBrainstorming(true);
      toast.info('Starting AI brainstorm... This may take 1-2 minutes. Please wait.');
      
      const response = await api.post(`/creator-outreach/${id}/brainstorm`, {
        research_input: about
      }, { timeout: 180000 }); // 3 minute timeout
      
      toast.success('Brainstorm complete! Check the Design Cost tab for results.');
      setBrainstorms(prev => [response.data, ...prev]);
      setExpandedBrainstorm(response.data.id);
      setActiveTab('design');
    } catch (error) {
      console.error('Brainstorm error:', error);
      
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.response?.status === 520 || error.response?.status === 504) {
        toast.warning('Request timed out, but the brainstorm may still be processing. Checking status in 30 seconds...', { duration: 10000 });
        
        // Auto-refresh brainstorms after a delay
        setTimeout(async () => {
          try {
            await fetchBrainstorms();
            toast.success('Brainstorm list updated! Check for new results.');
          } catch (e) {
            console.error('Failed to refresh brainstorms:', e);
          }
        }, 30000);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate concepts. Please try again.');
      }
    } finally {
      setBrainstorming(false);
    }
  };

  const handleDeleteBrainstorm = async (brainstormId) => {
    try {
      await api.delete(`/creator-outreach/${id}/brainstorm/${brainstormId}`);
      setBrainstorms(prev => prev.filter(b => b.id !== brainstormId));
      toast.success('Brainstorm deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete brainstorm');
    }
  };

  const getBrainstormRenderUrl = (brainstormId, conceptId) => {
    return `${API_BASE}/api/creator-outreach/${id}/brainstorm/${brainstormId}/render/${conceptId}?token=${authToken}`;
  };

  // Design file upload handlers
  const handleDesignFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingDesignFile(true);
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        await api.post(`/creator-outreach/${id}/design-files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success('Design file(s) uploaded!');
      fetchCreator();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingDesignFile(false);
      if (designFileInputRef.current) {
        designFileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDesignFile = async (fileId) => {
    try {
      await api.delete(`/creator-outreach/${id}/design-files/${fileId}`);
      toast.success('File deleted');
      fetchCreator();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    }
  };

  const getDesignFileUrl = (file) => {
    return `${API_BASE}/api/creator-outreach/${id}/design-files/${file.id}/download?token=${authToken}`;
  };

  const getDesignFilePreviewUrl = (file) => {
    return `${API_BASE}/api/creator-outreach/${id}/design-files/${file.id}/stream?token=${authToken}`;
  };

  const formatFollowers = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toString();
  };

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraReady(true);
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError('Could not access camera/microphone. Please allow permissions.');
      toast.error('Camera access denied');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    
    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType: 'video/webm;codecs=vp9,opus'
    });
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
    };
    
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopCamera();
    }
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    chunksRef.current = [];
  };

  const uploadVideo = async () => {
    if (!recordedBlob) return;
    
    try {
      setUploadingVideo(true);
      const formData = new FormData();
      formData.append('file', recordedBlob, 'pitch-video.webm');
      
      await api.post(`/creator-outreach/${id}/pitch-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Pitch video uploaded!');
      discardRecording();
      fetchCreator();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const deletePitchVideo = async () => {
    try {
      setDeletingVideo(true);
      await api.delete(`/creator-outreach/${id}/pitch-video`);
      toast.success('Video deleted');
      fetchCreator();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete video');
    } finally {
      setDeletingVideo(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getVideoUrl = () => {
    if (!creator?.pitch?.video) return null;
    return `${API_BASE}/api/creator-outreach/${id}/pitch-video/stream?token=${authToken}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!creator) return null;

  const tabs = [
    { id: 'about', label: 'About', icon: User },
    { id: 'design', label: 'Design Cost', icon: DollarSign },
    { id: 'pitch', label: 'Pitch', icon: MessageSquare },
  ];

  return (
    <div className="space-y-6" data-testid="creator-pack-detail">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/creator-outreach')}
        className="text-zinc-600 hover:text-zinc-900 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Creator Outreach
      </Button>

      {/* Creator Profile Header */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500 h-24" />
        <CardContent className="relative pt-0 pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 -mt-12">
            {/* Profile Picture */}
            <div className="relative">
              <img
                src={creator.instagram_data?.profile_pic_url || `https://unavatar.io/instagram/${creator.instagram_handle}`}
                alt={creator.instagram_handle}
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${creator.instagram_handle}&background=D4AF37&color=fff&size=128`;
                }}
              />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-full flex items-center justify-center border-2 border-white">
                <Instagram className="w-4 h-4 text-white" />
              </div>
            </div>

            {/* Creator Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-semibold text-zinc-900">@{creator.instagram_handle}</h1>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-32 h-8" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pitched">Pitched</SelectItem>
                    <SelectItem value="responded">Responded</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {creator.instagram_data?.full_name && (
                <p className="text-zinc-600">{creator.instagram_data.full_name}</p>
              )}
              
              {/* Followers Count */}
              <div className="flex items-center gap-2 mt-2">
                <Users className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-700">
                  {formatFollowers(followersCount)} followers
                </span>
              </div>
              
              {/* Social Links */}
              <div className="flex items-center gap-3 mt-2">
                <a
                  href={`https://instagram.com/${creator.instagram_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-pink-600 hover:text-pink-700 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                  <span>Instagram</span>
                </a>
                {tiktokHandle && (
                  <a
                    href={`https://tiktok.com/@${tiktokHandle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-zinc-800 hover:text-zinc-900 transition-colors"
                  >
                    <TikTokIcon className="w-4 h-4" />
                    <span>TikTok</span>
                  </a>
                )}
                {youtubeHandle && (
                  <a
                    href={youtubeHandle.includes('youtube.com') ? youtubeHandle : `https://youtube.com/@${youtubeHandle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 transition-colors"
                  >
                    <YouTubeIcon className="w-4 h-4" />
                    <span>YouTube</span>
                  </a>
                )}
              </div>
              
              {creator.instagram_data?.bio && (
                <p className="text-sm text-zinc-500 mt-2 max-w-xl">{creator.instagram_data.bio}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBrainstorm}
                disabled={brainstorming}
                className="border-yellow-400 text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700"
                data-testid="brainstorm-btn"
              >
                {brainstorming ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Brainstorm
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(`https://instagram.com/${creator.instagram_handle}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Profile
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#D4AF37] hover:bg-amber-600 text-white"
                data-testid="save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Floating Tab Menu */}
      <div className="sticky top-4 z-10">
        <Card className="inline-flex p-1 shadow-lg">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-[#D4AF37] hover:bg-amber-600 text-white' : ''}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </Button>
            ))}
          </div>
        </Card>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* About Tab */}
        {activeTab === 'about' && (
          <Card data-testid="about-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#D4AF37]" />
                About This Creator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Social Handles & Followers */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-zinc-500" />
                    Instagram Followers
                  </label>
                  <Input
                    type="number"
                    placeholder="e.g., 50000"
                    value={followersCount}
                    onChange={(e) => setFollowersCount(e.target.value)}
                    data-testid="followers-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Enter follower count manually
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-2 flex items-center gap-2">
                    <TikTokIcon className="w-4 h-4 text-zinc-800" />
                    TikTok Handle
                  </label>
                  <Input
                    placeholder="@username"
                    value={tiktokHandle}
                    onChange={(e) => setTiktokHandle(e.target.value)}
                    data-testid="tiktok-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-2 flex items-center gap-2">
                    <YouTubeIcon className="w-4 h-4 text-red-600" />
                    YouTube Handle/URL
                  </label>
                  <Input
                    placeholder="@channel or youtube.com/..."
                    value={youtubeHandle}
                    onChange={(e) => setYoutubeHandle(e.target.value)}
                    data-testid="youtube-input"
                  />
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-2 block">
                  Notes & Research
                </label>
                <Textarea
                  placeholder="Add notes about this creator... their interests, content style, engagement patterns, why they'd be a good fit, etc."
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  rows={8}
                  className="resize-none"
                  data-testid="about-textarea"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Use this space to research and document information that will help craft a personalized pitch.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Design Cost Tab */}
        {activeTab === 'design' && (
          <Card data-testid="design-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                Design Cost Estimation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-2 block">
                    Estimated Cost
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={designCostAmount}
                      onChange={(e) => setDesignCostAmount(e.target.value)}
                      className="pl-8"
                      data-testid="design-cost-input"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-2 block">
                  Cost Breakdown & Notes
                </label>
                <Textarea
                  placeholder="Detail the design costs... product costs, shipping, packaging, creative fees, etc."
                  value={designCostNotes}
                  onChange={(e) => setDesignCostNotes(e.target.value)}
                  rows={6}
                  className="resize-none"
                  data-testid="design-notes-textarea"
                />
              </div>
              
              {/* CAD/Render Files Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-zinc-900">CAD & Render Files</h3>
                    <p className="text-xs text-zinc-500">Upload design files, CAD models, renders, or reference images</p>
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={designFileInputRef}
                      onChange={handleDesignFileUpload}
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,.pdf,.step,.stp,.stl,.obj,.ai,.eps,.svg,.psd"
                      className="hidden"
                      data-testid="design-file-input"
                    />
                    <Button
                      onClick={() => designFileInputRef.current?.click()}
                      disabled={uploadingDesignFile}
                      variant="outline"
                      size="sm"
                      data-testid="upload-design-file-btn"
                    >
                      {uploadingDesignFile ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Files
                    </Button>
                  </div>
                </div>
                
                {/* File Grid */}
                {designFiles.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {designFiles.map((file) => (
                      <div
                        key={file.id}
                        className="relative group border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50"
                        data-testid={`design-file-${file.id}`}
                      >
                        {/* Preview */}
                        <div className="aspect-square relative">
                          {file.is_image ? (
                            <img
                              src={getDesignFilePreviewUrl(file)}
                              alt={file.original_name}
                              className="w-full h-full object-cover"
                            />
                          ) : file.is_video ? (
                            <video
                              src={getDesignFilePreviewUrl(file)}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-100 p-4">
                              <ImageIcon className="w-8 h-8 text-zinc-400 mb-2" />
                              <span className="text-xs text-zinc-500 text-center truncate max-w-full">
                                {file.original_name}
                              </span>
                            </div>
                          )}
                          
                          {/* Video play indicator */}
                          {file.is_video && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Film className="w-8 h-8 text-white" />
                            </div>
                          )}
                        </div>
                        
                        {/* Actions Overlay */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a
                            href={getDesignFileUrl(file)}
                            download={file.original_name}
                            className="p-1.5 bg-white/90 rounded-md shadow hover:bg-white"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="w-4 h-4 text-zinc-700" />
                          </a>
                          <button
                            onClick={() => handleDeleteDesignFile(file.id)}
                            className="p-1.5 bg-white/90 rounded-md shadow hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                        
                        {/* File Info */}
                        <div className="p-2 border-t border-zinc-200 bg-white">
                          <p className="text-xs text-zinc-700 truncate font-medium">{file.original_name}</p>
                          <p className="text-xs text-zinc-500">
                            {(file.file_size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center">
                    <ImageIcon className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                    <p className="text-sm text-zinc-600 mb-1">No design files uploaded yet</p>
                    <p className="text-xs text-zinc-500">
                      Upload CAD files, renders, images, or videos for this collaboration
                    </p>
                  </div>
                )}
              </div>

              {/* AI Brainstorm Results Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-500" />
                      AI-Generated Jewelry Concepts
                    </h3>
                    <p className="text-xs text-zinc-500">Brainstorm unique jewelry designs based on creator research</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBrainstorm}
                    disabled={brainstorming}
                    className="border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                    data-testid="brainstorm-design-btn"
                  >
                    {brainstorming ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        New Brainstorm
                      </>
                    )}
                  </Button>
                </div>

                {brainstorms.length > 0 ? (
                  <div className="space-y-4">
                    {brainstorms.map((brainstorm) => (
                      <div 
                        key={brainstorm.id} 
                        className="border border-zinc-200 rounded-lg overflow-hidden bg-white"
                        data-testid={`brainstorm-${brainstorm.id}`}
                      >
                        {/* Brainstorm Header */}
                        <div 
                          className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-amber-50 cursor-pointer hover:from-yellow-100 hover:to-amber-100 transition-colors"
                          onClick={() => setExpandedBrainstorm(expandedBrainstorm === brainstorm.id ? null : brainstorm.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                              <Gem className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-zinc-900 text-sm">
                                {brainstorm.concepts?.length || 0} Jewelry Concepts
                              </p>
                              <p className="text-xs text-zinc-500">
                                {new Date(brainstorm.created_at).toLocaleDateString()} at {new Date(brainstorm.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {brainstorm.status === 'completed' && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Completed</span>
                            )}
                            {brainstorm.status === 'generating' && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Generating
                              </span>
                            )}
                            {brainstorm.status === 'failed' && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Failed</span>
                            )}
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleDeleteBrainstorm(brainstorm.id); }}
                              className="p-1 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                            {expandedBrainstorm === brainstorm.id ? (
                              <ChevronUp className="w-5 h-5 text-zinc-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-zinc-400" />
                            )}
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {expandedBrainstorm === brainstorm.id && brainstorm.concepts && (
                          <div className="p-4 border-t border-zinc-100">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {brainstorm.concepts.map((concept, idx) => {
                                const render = brainstorm.renders?.find(r => r.concept_id === concept.id);
                                return (
                                  <div 
                                    key={concept.id || idx} 
                                    className="border border-zinc-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                                    data-testid={`concept-${concept.id}`}
                                  >
                                    {/* Render Image */}
                                    {render?.image_url ? (
                                      <div className="aspect-square relative bg-zinc-100">
                                        <img
                                          src={getBrainstormRenderUrl(brainstorm.id, concept.id)}
                                          alt={concept.name}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                        <div className="hidden w-full h-full items-center justify-center bg-zinc-100">
                                          <ImageIcon className="w-8 h-8 text-zinc-300" />
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="aspect-square bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center">
                                        <Gem className="w-12 h-12 text-zinc-300" />
                                      </div>
                                    )}

                                    {/* Concept Details */}
                                    <div className="p-3 space-y-2">
                                      <div className="flex items-start justify-between gap-2">
                                        <div>
                                          <span className="text-xs font-medium text-amber-600 uppercase">{concept.style}</span>
                                          <h4 className="font-semibold text-zinc-900 text-sm">{concept.name}</h4>
                                        </div>
                                        <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded capitalize">{concept.type}</span>
                                      </div>
                                      
                                      <p className="text-xs text-zinc-600 line-clamp-2">{concept.summary}</p>
                                      
                                      {concept.design && (
                                        <div className="text-xs space-y-1 pt-2 border-t border-zinc-100">
                                          <p><span className="font-medium text-zinc-700">Metal:</span> {concept.design.metal}</p>
                                          {concept.design.diamonds && (
                                            <p><span className="font-medium text-zinc-700">Diamonds:</span> {concept.design.diamonds}</p>
                                          )}
                                          {concept.design.gemstones && concept.design.gemstones !== 'None' && (
                                            <p><span className="font-medium text-zinc-700">Gemstones:</span> {concept.design.gemstones}</p>
                                          )}
                                        </div>
                                      )}
                                      
                                      {concept.estimated_stone_cost && (
                                        <p className="text-xs font-medium text-emerald-600 pt-1">
                                          Est. Cost: {concept.estimated_stone_cost}
                                        </p>
                                      )}

                                      {concept.personal_connection && (
                                        <p className="text-xs text-zinc-500 italic pt-1 border-t border-zinc-100">
                                          "{concept.personal_connection}"
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* CAD Briefs - Expandable */}
                            <div className="mt-4 pt-4 border-t border-zinc-200">
                              <details className="group">
                                <summary className="cursor-pointer text-sm font-medium text-zinc-700 flex items-center gap-2 hover:text-zinc-900">
                                  <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                                  CAD Designer Briefs
                                </summary>
                                <div className="mt-3 space-y-3">
                                  {brainstorm.concepts.map((concept, idx) => (
                                    <div key={concept.id || idx} className="bg-zinc-50 rounded-lg p-3">
                                      <p className="text-xs font-medium text-amber-600 mb-1">{concept.name}</p>
                                      <p className="text-xs text-zinc-600 whitespace-pre-wrap">{concept.cad_brief}</p>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-yellow-200 rounded-lg p-8 text-center bg-yellow-50/30">
                    <Sparkles className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
                    <p className="text-sm text-zinc-700 mb-1">No brainstorms yet</p>
                    <p className="text-xs text-zinc-500 mb-4">
                      Add research notes in the About tab, then click "Brainstorm" to generate custom jewelry concepts
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBrainstorm}
                      disabled={brainstorming || !about || about.length < 20}
                      className="border-yellow-400 text-yellow-600 hover:bg-yellow-100"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Start Brainstorming
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pitch Tab */}
        {activeTab === 'pitch' && (
          <div className="space-y-6">
            {/* In-mail Video Section */}
            <Card data-testid="pitch-video-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-[#D4AF37]" />
                  In-mail Video Pitch
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Existing Video */}
                {creator.pitch?.video && !recordedUrl && (
                  <div className="space-y-4">
                    <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-w-2xl">
                      <video
                        src={getVideoUrl()}
                        controls
                        className="w-full h-full"
                        crossOrigin="anonymous"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => startCamera()}
                        disabled={cameraReady}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Record New Video
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={deletePitchVideo}
                        disabled={deletingVideo}
                      >
                        {deletingVideo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Delete Video
                      </Button>
                    </div>
                  </div>
                )}

                {/* Recording Interface */}
                {!creator.pitch?.video && !recordedUrl && (
                  <div className="space-y-4">
                    {!cameraReady ? (
                      <div className="aspect-video max-w-2xl bg-zinc-900 rounded-lg flex flex-col items-center justify-center">
                        <Camera className="w-12 h-12 text-zinc-600 mb-4" />
                        <p className="text-zinc-400 mb-4">Record a personalized video message for this creator</p>
                        {cameraError && (
                          <p className="text-red-400 text-sm mb-4">{cameraError}</p>
                        )}
                        <Button onClick={startCamera} className="bg-[#D4AF37] hover:bg-amber-600 text-white">
                          <Camera className="w-4 h-4 mr-2" />
                          Start Camera
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="relative aspect-video max-w-2xl bg-black rounded-lg overflow-hidden">
                          <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className="w-full h-full object-cover mirror"
                            style={{ transform: 'scaleX(-1)' }}
                          />
                          {isRecording && (
                            <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              Recording...
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!isRecording ? (
                            <Button onClick={startRecording} className="bg-red-500 hover:bg-red-600 text-white">
                              <Play className="w-4 h-4 mr-2" />
                              Start Recording
                            </Button>
                          ) : (
                            <Button onClick={stopRecording} variant="destructive">
                              <Square className="w-4 h-4 mr-2" />
                              Stop Recording
                            </Button>
                          )}
                          <Button variant="outline" onClick={stopCamera}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Preview & Upload */}
                {recordedUrl && (
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-zinc-700">Preview your recording:</p>
                    <div className="relative aspect-video max-w-2xl bg-black rounded-lg overflow-hidden">
                      <video
                        src={recordedUrl}
                        controls
                        className="w-full h-full"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={uploadVideo}
                        disabled={uploadingVideo}
                        className="bg-[#D4AF37] hover:bg-amber-600 text-white"
                      >
                        {uploadingVideo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Save Video
                      </Button>
                      <Button variant="outline" onClick={discardRecording}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Discard
                      </Button>
                      <Button variant="outline" onClick={startCamera}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Re-record
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Copy Section */}
            <Card data-testid="email-copy-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#D4AF37]" />
                  Email Copy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder={`Hi [Creator Name],\n\nI came across your content and absolutely loved...\n\nWe're a jewelry brand called ${creator.brand === 'thelocaljewel' ? 'The Local Jewel' : 'Justlabs'} and I think there could be a great opportunity for us to collaborate...\n\nBest,\n[Your Name]`}
                  value={emailCopy}
                  onChange={(e) => setEmailCopy(e.target.value)}
                  rows={12}
                  className="resize-none font-mono text-sm"
                  data-testid="email-copy-textarea"
                />
                {emailCopy && (
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(emailCopy, 'Email copy')}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Email
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* DM Copy Section */}
            <Card data-testid="dm-copy-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
                  DM Copy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Hey! Love your content. We're a jewelry brand and would love to chat about a potential collab - no strings attached. Mind if I share more details? [emoji]"
                  value={dmCopy}
                  onChange={(e) => setDmCopy(e.target.value)}
                  rows={6}
                  className="resize-none"
                  data-testid="dm-copy-textarea"
                />
                <p className="text-xs text-zinc-500">
                  Keep DMs short and casual. The goal is to start a conversation, not pitch everything at once.
                </p>
                {dmCopy && (
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(dmCopy, 'DM copy')}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy DM
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorPackDetail;
